import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, LoadingSpinner } from '../../components/atoms';
import { useDocumentaryActions } from '../../hooks/useDocumentaries';
import type { DocumentaryDetailsResponse } from '../../types/api/documentary';
import {
  formatDateForDisplay,
  formatDateTimeForDisplay,
  isSubtypeApplicable,
  getDocumentarySubtypeLabel,
} from '../../types/api/documentary';
import { resolveAssetUrl } from '../../utils/url';
import toast from 'react-hot-toast';

/**
 * DocumentaryDetailPage Component
 *
 * Page for viewing documentary details:
 * - Metadata
 * - Thumbnail
 * - Video variants (read-only)
 */
export const DocumentaryDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { documentaryId } = useParams<{ documentaryId: string }>();
  const { getDocumentary } = useDocumentaryActions();

  const [details, setDetails] = useState<DocumentaryDetailsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!documentaryId) {
      toast.error('Documentary ID is required');
      navigate('/pages/documentaries');
      return;
    }

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getDocumentary(documentaryId);
        setDetails(data);
      } catch (err: any) {
        console.error('Failed to load documentary:', err);
        setError(err.message || 'Failed to load documentary');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [documentaryId, navigate, getDocumentary]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!details || !details.documentary) {
    return (
      <div className="text-center py-12">
        {error ? (
          <>
            <p className="text-red-600 mb-4">{error}</p>
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="primary"
                onClick={() => window.location.reload()}
                className="mt-4"
              >
                Retry
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/pages/documentaries')}
                className="mt-4"
              >
                Back to Documentaries
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-gray-600 mb-4">Documentary not found</p>
            <Button
              variant="outline"
              onClick={() => navigate('/pages/documentaries')}
              className="mt-4"
            >
              Back to Documentaries
            </Button>
          </>
        )}
      </div>
    );
  }

  const documentary = details.documentary;

  const statusColors: Record<string, string> = {
    ACTIVE: 'bg-green-100 text-green-800',
    INACTIVE: 'bg-gray-100 text-gray-800',
    PENDING: 'bg-yellow-100 text-yellow-800',
  };

  const accessColors: Record<string, string> = {
    FREE: 'bg-blue-100 text-blue-800',
    PREMIUM: 'bg-purple-100 text-purple-800',
    ENTERPRISE: 'bg-indigo-100 text-indigo-800',
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="text-left">
          <h1 className="text-2xl font-bold text-gray-900">Documentary Details</h1>
          <p className="text-gray-600 mt-1">View comprehensive documentary information</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/pages/documentaries')}
            leftIcon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            }
          >
            Back to Documentaries
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate(`/pages/documentaries/${documentary.contentId}/edit`)}
            leftIcon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            }
          >
            Edit Documentary
          </Button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Thumbnail Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-6">
            {/* Thumbnail */}
            <div className="relative mb-6">
              {documentary.thumbnailUrl ? (
                <img
                  src={resolveAssetUrl(documentary.thumbnailUrl)}
                  alt={documentary.title}
                  className="w-full aspect-[2/3] rounded-xl border border-gray-300 object-cover shadow-md"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className="w-full aspect-[2/3] bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl border border-gray-300 flex items-center justify-center shadow-md"
                style={{ display: documentary.thumbnailUrl ? 'none' : 'flex' }}
              >
                <div className="text-center">
                  <svg
                    className="w-20 h-20 text-gray-400 mx-auto mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-sm font-medium text-gray-500">No Thumbnail</p>
                </div>
              </div>
            </div>

            {/* Status Badges */}
            <div className="space-y-3">
              <div className="text-center">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Status
                </p>
                <span
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold ${
                    statusColors[documentary.status] || 'bg-gray-50 text-gray-700 border border-gray-200'
                  }`}
                >
                  {documentary.status}
                </span>
              </div>
              <div className="text-center">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Access Level
                </p>
                <span
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold ${
                    accessColors[documentary.accessLevel] ||
                    'bg-gray-50 text-gray-700 border border-gray-200'
                  }`}
                >
                  {documentary.accessLevel}
                </span>
              </div>
              <div className="text-center">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Type
                </p>
                <div className="flex flex-col gap-1 items-center">
                  <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                    {documentary.documentaryType?.replace(/_/g, ' ')}
                  </span>
                  {/* Subtype badge — show label when set, warning when missing for DHARMA_SPEECH */}
                  {isSubtypeApplicable(documentary.documentaryType) && (
                    documentary.documentarySubtype ? (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-600 border border-indigo-200">
                        {getDocumentarySubtypeLabel(documentary.documentarySubtype)}
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200"
                        title="No category set — won't appear under တရားတော် or သုတ္တန် in the mobile app"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59c.75 1.334-.213 2.987-1.742 2.987H3.481c-1.53 0-2.493-1.653-1.743-2.987L8.257 3.1zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        No category
                      </span>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-left">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{documentary.title}</h2>
            <p className="text-gray-600 leading-relaxed text-base">
              {documentary.description}
            </p>
          </div>

          {/* Documentary Details Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center pb-4">
              <svg
                className="w-5 h-5 mr-2 text-[#EAB308]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Documentary Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-[#EAB308]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Release Date
                  </p>
                  <p className="text-gray-900 font-medium">
                    {formatDateForDisplay(documentary.releaseDate)}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6M9 8h6m-9 8h.01M5 8h.01M5 12h.01M5 16h.01M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Topic
                  </p>
                  <p className="text-gray-900 font-medium">
                    {documentary.topic || '—'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM5 21a7 7 0 0114 0H5z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Expert / Speaker
                  </p>
                  <p className="text-gray-900 font-medium">
                    {documentary.expert || '—'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-yellow-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Upload Date
                  </p>
                  <p className="text-gray-900 font-medium">
                    {formatDateTimeForDisplay(documentary.uploadDate)}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5h18M9 3v4m6-4v4M5 9h14v10a2 2 0 01-2 2H7a2 2 0 01-2-2V9z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Duration (seconds)
                  </p>
                  <p className="text-gray-900 font-medium">
                    {documentary.duration ? `${documentary.duration} sec` : '—'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-indigo-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6l4 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Priority
                  </p>
                  <p className="text-gray-900 font-medium">
                    {documentary.priority ?? '—'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-indigo-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Language
                  </p>
                  <p className="text-gray-900 font-medium">
                    {documentary.languageName || '—'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Video Variants */}
          {details.videoVariants && details.videoVariants.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Video Files</h3>
              <div className="space-y-2">
                {details.videoVariants.map((variant) => (
                  <div
                    key={variant.fileId || variant.videoId || `${variant.videoType}-${variant.quality}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">
                        {variant.quality} - {variant.videoType}
                      </p>
                      {variant.isDefault && (
                        <span className="text-xs text-blue-600">Default</span>
                      )}
                      {variant.durationSeconds && (
                        <p className="text-xs text-gray-500">
                          {Math.floor(variant.durationSeconds / 60)}:
                          {(variant.durationSeconds % 60).toString().padStart(2, '0')}
                        </p>
                      )}
                    </div>
                    {variant.playbackUrl && (
                      <a
                        href={variant.playbackUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Play
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentaryDetailPage;

